import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DISABLE_USER_PERMISSION } from '@/entities/session';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { SidePanel } from '@/shared/ui/SidePanel';

import { statusLabelKey, statusTone } from '../model/access';

import type { Role, TenantUser } from '@/shared/api/generated/tenancy';

/* One person, in the side panel the design draws beside the table: their roles, whether they can
 * sign in, and removing them.
 *
 * **A side panel rather than a modal drawer**, so the table stays readable and choosing Manage on
 * another row moves the panel to that person.
 *
 * **Saving replaces the whole role set in one request**, and the two warnings say what a set means
 * before it is saved: no roles at all lets somebody sign in and do nothing, and admin with
 * data-steward removes segregation of duties for that person — the platform still refuses an
 * author's own signature, but there is nobody independent left to be the second one.
 *
 * **Disabling is refused for yourself** and the reason is on the button rather than a silent
 * absence, so a reader is not left guessing whether they lacked the permission instead.
 */

export interface UserDrawerProps {
  readonly user: TenantUser;
  readonly roles: readonly Role[];
  readonly callerSubject: string;
  readonly mayDisable: boolean;
  readonly mayDelete: boolean;
  readonly onClose: () => void;
  readonly onSaveRoles: (roleNames: readonly string[]) => void;
  readonly onSetDisabled: (disabled: boolean) => void;
  readonly onDelete: () => void;
  readonly saved: boolean;
  readonly saving: boolean;
  readonly error: Error | null;
  readonly deleting: boolean;
  readonly deleteError: Error | null;
}

export function UserDrawer({
  user,
  roles,
  callerSubject,
  mayDisable,
  mayDelete,
  onClose,
  onSaveRoles,
  onSetDisabled,
  onDelete,
  saved,
  saving,
  error,
  deleting,
  deleteError,
}: UserDrawerProps): ReactElement {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<readonly string[]>(user.roles);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const changed =
    selected.length !== user.roles.length ||
    selected.some((name) => !user.roles.includes(name));
  const isSelf = user.subject === callerSubject;
  const disableBlocked = !user.disabled && (isSelf || !mayDisable);

  const head = (
    <div className="lq-ua__panelHead">
      <h2 className="lq-ua__panelName">{user.display_name}</h2>
      <span className="lq-ua__status" data-tone={statusTone(user)}>
        {t(statusLabelKey(user))}
      </span>
    </div>
  );

  return (
    <SidePanel label={user.display_name} onClose={onClose} head={head}>
      <fieldset className="lq-ua__rolesSet">
        <legend className="lq-ua__legend">{t('admin.drawer.roles')}</legend>
        <p className="lq-ua__hint">{t('admin.drawer.rolesHint')}</p>
        {roles.map((role) => (
          <label key={role.name} className="lq-ua__roleOption">
            <input
              type="checkbox"
              checked={selected.includes(role.name)}
              onChange={(event) => {
                setSelected((current) =>
                  event.target.checked
                    ? [...current, role.name]
                    : current.filter((name) => name !== role.name),
                );
              }}
            />
            <span>
              <span className="lq-ua__roleName">{role.name}</span>
              {role.description !== '' && (
                <span className="lq-ua__roleDesc">{role.description}</span>
              )}
            </span>
          </label>
        ))}
      </fieldset>

      {selected.length === 0 && (
        <p className="lq-ua__warn">{t('admin.drawer.noRolesWarning')}</p>
      )}
      {selected.includes('admin') && selected.includes('data-steward') && (
        <p className="lq-ua__warn">{t('admin.drawer.segregationWarning')}</p>
      )}
      {error !== null && (
        <p className="lq-ua__error" role="alert">
          {error.message}
        </p>
      )}
      {saved && !changed && error === null && (
        <p className="lq-ua__saved" role="status">
          {t('admin.drawer.saved', { name: user.display_name })}
        </p>
      )}

      <div className="lq-ua__row">
        <Button
          disabled={!changed}
          onClick={() => {
            setSelected(user.roles);
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          tone="primary"
          busy={saving}
          disabled={!changed}
          onClick={() => {
            onSaveRoles(selected);
          }}
        >
          {t('admin.drawer.save')}
        </Button>
      </div>

      <section className="lq-ua__group">
        <h3 className="lq-ua__groupTitle">{t('admin.drawer.access')}</h3>
        <Button
          size="compact"
          className="lq-ua__start"
          busy={saving}
          disabled={disableBlocked}
          title={
            disableBlocked
              ? isSelf
                ? t('admin.drawer.cannotDisableSelf')
                : t('admin.drawer.needs', { permission: DISABLE_USER_PERMISSION })
              : undefined
          }
          onClick={() => {
            onSetDisabled(!user.disabled);
          }}
        >
          {user.disabled ? t('admin.drawer.enable') : t('admin.drawer.disable')}
        </Button>
        <p className="lq-ua__hint">
          {user.disabled ? t('admin.drawer.enableHint') : t('admin.drawer.disableHint')}
        </p>
        {isSelf && <p className="lq-ua__hint">{t('admin.drawer.cannotDisableSelf')}</p>}
      </section>

      {mayDelete && (
        <section className="lq-ua__group">
          <h3 className="lq-ua__groupTitle">{t('admin.drawer.remove')}</h3>
          <p className="lq-ua__hint">{t('admin.drawer.deleteNote')}</p>
          <Button
            size="compact"
            className="lq-ua__start lq-ua__danger"
            busy={deleting}
            onClick={() => {
              setConfirmingDelete(true);
            }}
          >
            {t('admin.drawer.delete')}
          </Button>
        </section>
      )}

      <ConfirmDialog
        open={confirmingDelete}
        title={t('admin.drawer.confirmDeleteTitle')}
        message={
          <>
            <ul className="lq-consequences">
              <li>{t('admin.drawer.consequence.signIn')}</li>
              <li>{t('admin.drawer.consequence.roles')}</li>
              <li>{t('admin.drawer.consequence.reserved')}</li>
            </ul>
            <p className="lq-ua__hint">{t('admin.drawer.consequence.audit')}</p>
          </>
        }
        confirmLabel={t('admin.drawer.confirmDelete')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={deleting}
        /* Refusals — your own account, the last remaining admin — arrive with the server's own
           sentence, readable where the question was asked. */
        {...(deleteError === null ? {} : { error: deleteError.message })}
        onConfirm={onDelete}
        onCancel={() => {
          setConfirmingDelete(false);
        }}
      />
    </SidePanel>
  );
}
